// Changes inside an existing PDF, made on the person's own computer: the original layout, logos and signatures stay.
// VERA proposes the changes (she has read the PDF); this applies them with pdf-lib, and finds where text sits with PDF.js.
// Replacing text covers the old words with white and writes the new words in the same place and size, in Helvetica.
// Nothing leaves this computer here; the person checks the edited PDF before using it.

export type PdfEditOp = {
  type: "replace_text" | "fill_field" | "add_text" | "delete_pages" | "rotate_pages";
  find?: string | null; replace?: string | null; field?: string | null; value?: string | null;
  page?: number | null; text?: string | null; position?: "top" | "bottom" | null; pages?: number[] | null; degrees?: number | null;
};
export type PdfEditResult = { op: PdfEditOp; ok: boolean; count?: number };

type Hit = { page: number; x: number; y: number; w: number; size: number };
type PdfJs = typeof import("pdfjs-dist");

/** PDF.js in the browser, with its worker from this app. (Tests pass the Node build instead.) */
async function browserPdfJs(): Promise<PdfJs> {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
  return pdfjs;
}

/** Where a piece of text sits, page by page, in PDF coordinates (lines rebuilt from PDF.js text items). */
async function locate(bytes: Uint8Array, find: string, pdfjs: PdfJs): Promise<Hit[]> {
  const doc = await pdfjs.getDocument({ data: bytes.slice(), isEvalSupported: false }).promise;
  const hits: Hit[] = [];
  const norm = (s: string) => s.replace(/\s+/g, " ");
  const target = norm(find).trim();
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const content = await page.getTextContent();
    // Group text items into lines by their baseline.
    const lines = new Map<number, { x: number; w: number; str: string; size: number }[]>();
    for (const it of content.items as { str: string; transform: number[]; width: number }[]) {
      if (!it.str) continue;
      const [, , c, d, e, f] = it.transform;
      const key = Math.round(f);
      const size = Math.hypot(c, d) || 10;
      (lines.get(key) ?? lines.set(key, []).get(key)!).push({ x: e, w: it.width, str: it.str, size });
    }
    for (const [y, items] of lines) {
      items.sort((a, b) => a.x - b.x);
      // The line as one string, remembering which item each character came from.
      let text = ""; const owner: number[] = [];
      items.forEach((it, i) => {
        if (i > 0 && it.x - (items[i - 1].x + items[i - 1].w) > it.size * 0.15 && !text.endsWith(" ")) { text += " "; owner.push(i); }
        // Spaces collapse to one, so "15  mars" in the PDF still matches "15 mars".
        for (const ch of it.str) { const space = /\s/.test(ch); if (space && text.endsWith(" ")) continue; text += space ? " " : ch; owner.push(i); }
      });
      const line = text;
      for (let at = line.indexOf(target); at >= 0; at = line.indexOf(target, at + target.length)) {
        const xOf = (pos: number, end: boolean) => {
          const i = owner[Math.min(pos, owner.length - 1)], it = items[i];
          const first = owner.indexOf(i), len = owner.lastIndexOf(i) - first + 1;
          return it.x + it.w * ((pos - first + (end ? 1 : 0)) / Math.max(1, len));
        };
        const x0 = xOf(at, false), x1 = xOf(at + target.length - 1, true);
        hits.push({ page: p, x: x0, y, w: Math.max(1, x1 - x0), size: items[owner[at]].size });
      }
    }
  }
  await doc.destroy();
  return hits;
}

/** Helvetica can write Latin letters, including Albanian ë and ç; anything else becomes a close letter or a space. */
const writable = (s: string) => s.normalize("NFC").replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/[–—]/g, "-").replace(/[^\x20-\x7E -ÿ]/g, " ");

export async function applyPdfEdits(original: Uint8Array, ops: PdfEditOp[], loadPdfJs: () => Promise<PdfJs> = browserPdfJs): Promise<{ bytes: Uint8Array; results: PdfEditResult[] }> {
  const { PDFDocument, StandardFonts, rgb, degrees, PDFTextField, PDFCheckBox, PDFDropdown, PDFRadioGroup } = await import("pdf-lib");
  const pdf = await PDFDocument.load(original, { ignoreEncryption: true });
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const pages = pdf.getPages();
  const results: PdfEditResult[] = [];
  // Page numbers refer to the original PDF, so pages are removed last.
  const order = [...ops].sort((a, b) => Number(a.type === "delete_pages") - Number(b.type === "delete_pages"));
  for (const op of order) {
    try {
      if (op.type === "replace_text" && op.find) {
        const hits = await locate(original, op.find, await loadPdfJs());
        const text = writable(op.replace ?? "");
        for (const h of hits) {
          const page = pages[h.page - 1];
          page.drawRectangle({ x: h.x - 1, y: h.y - h.size * 0.28, width: h.w + 2, height: h.size * 1.22, color: rgb(1, 1, 1) });
          // Same size as the old words; a little smaller if the new words are much longer, so they don't run over.
          let size = h.size;
          const need = font.widthOfTextAtSize(text, size);
          if (need > h.w * 1.6) size = Math.max(h.size * 0.7, (h.size * h.w * 1.6) / need);
          if (text) page.drawText(text, { x: h.x, y: h.y, size, font, color: rgb(0, 0, 0) });
        }
        results.push({ op, ok: hits.length > 0, count: hits.length });
      } else if (op.type === "fill_field" && op.field) {
        const form = pdf.getForm();
        const want = op.field.toLowerCase().trim();
        const field = form.getFields().find((f) => f.getName().toLowerCase() === want) ?? form.getFields().find((f) => f.getName().toLowerCase().includes(want));
        const value = op.value ?? "";
        if (field instanceof PDFTextField) field.setText(writable(value));
        else if (field instanceof PDFCheckBox) (/^(yes|po|true|x|1|checked)$/i.test(value.trim()) ? field.check() : field.uncheck());
        else if (field instanceof PDFDropdown) field.select(value);
        else if (field instanceof PDFRadioGroup) field.select(value);
        results.push({ op, ok: Boolean(field) });
        if (field) form.updateFieldAppearances(font);
      } else if (op.type === "add_text" && op.text) {
        const page = pages[(op.page ?? 1) - 1];
        if (page) {
          const { width, height } = page.getSize();
          const text = writable(op.text);
          const size = 10, w = font.widthOfTextAtSize(text, size);
          page.drawText(text, { x: Math.max(36, (width - w) / 2), y: op.position === "top" ? height - 28 : 20, size, font, color: rgb(0.15, 0.15, 0.15) });
        }
        results.push({ op, ok: Boolean(page) });
      } else if (op.type === "rotate_pages" && op.pages?.length) {
        let n = 0;
        for (const p of op.pages) { const page = pages[p - 1]; if (page) { page.setRotation(degrees((page.getRotation().angle + (op.degrees ?? 90)) % 360)); n++; } }
        results.push({ op, ok: n > 0, count: n });
      } else if (op.type === "delete_pages" && op.pages?.length) {
        const remove = [...new Set(op.pages)].filter((p) => p >= 1 && p <= pages.length).sort((a, b) => b - a);
        if (remove.length >= pdf.getPageCount()) { results.push({ op, ok: false }); continue; } // never delete every page
        for (const p of remove) pdf.removePage(p - 1);
        results.push({ op, ok: remove.length > 0, count: remove.length });
      } else {
        results.push({ op, ok: false });
      }
    } catch {
      results.push({ op, ok: false });
    }
  }
  // Results in the order VERA gave the changes.
  return { bytes: await pdf.save(), results: ops.map((o) => results.find((r) => r.op === o)!) };
}

export const dataUrlToBytes = (dataUrl: string) => Uint8Array.from(atob(dataUrl.slice(dataUrl.indexOf(",") + 1)), (c) => c.charCodeAt(0));
